import { useState, useCallback } from 'react'
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import type {
  BranchableLessonConfig,
  ComponentConfigV2,
} from '@/types/configurationV2'
import BranchSelector from './BranchSelector'
import ComponentCard from './ComponentCard'
import ComponentAdder from './ComponentAdder'
import LessonFlowPreview from './LessonFlowPreview'

interface LessonTabProps {
  config: BranchableLessonConfig
  onChange: (config: BranchableLessonConfig) => void
  renderOptionsEditor?: (
    component: ComponentConfigV2,
    onUpdate: (updated: ComponentConfigV2) => void
  ) => React.ReactNode
}

const EMPTY_LESSON_CONFIG: BranchableLessonConfig = {
  belongsTo: null,
  default: { components: [] },
  overrides: [],
}

export default function LessonTab({ config: rawConfig, onChange, renderOptionsEditor }: LessonTabProps) {
  const config = rawConfig ?? EMPTY_LESSON_CONFIG
  const [activeBranch, setActiveBranch] = useState<number | 'default'>('default')

  const getActiveComponents = (): ComponentConfigV2[] => {
    if (activeBranch === 'default') return config.default?.components ?? []
    const override = config.overrides?.find((o) => o.contentId === activeBranch)
    return override?.components ?? config.default?.components ?? []
  }

  const components = getActiveComponents()
  const activeBranchLabel =
    activeBranch === 'default'
      ? '기본'
      : config.overrides?.find((o) => o.contentId === activeBranch)?.contentName ?? `override #${activeBranch}`

  const updateComponents = useCallback(
    (newComponents: ComponentConfigV2[]) => {
      const resequenced = newComponents.map((c, idx) => ({ ...c, sortOrder: idx }))
      if (activeBranch === 'default') {
        onChange({ ...config, default: { components: resequenced } })
      } else {
        const overrides = (config.overrides ?? []).map((o) =>
          o.contentId === activeBranch ? { ...o, components: resequenced } : o
        )
        onChange({ ...config, overrides })
      }
    },
    [config, activeBranch, onChange]
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = components.findIndex((c) => c.id === active.id)
    const newIndex = components.findIndex((c) => c.id === over.id)
    updateComponents(arrayMove(components, oldIndex, newIndex))
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <span className="text-sm font-bold">Lesson 레벨 — 컴포넌트 순서</span>
          <span className="text-xs text-gray-400 ml-2">통으로 구성 (pre/post 구분 없음)</span>
        </div>
        <BranchSelector
          belongsTo={config.belongsTo}
          onBelongsToChange={(lvl) => onChange({ ...config, belongsTo: lvl })}
          overrideNames={config.overrides.map((o) => ({
            contentId: o.contentId,
            contentName: o.contentName,
          }))}
          activeBranch={activeBranch}
          onBranchChange={setActiveBranch}
        />
      </div>

      <div className="space-y-2">
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={components.map((c) => c.id)} strategy={verticalListSortingStrategy}>
            {components.map((comp, idx) => (
              <ComponentCard
                key={comp.id}
                component={comp}
                onUpdate={(updated) => {
                  const newComps = [...components]
                  newComps[idx] = updated
                  updateComponents(newComps)
                }}
                onRemove={() => {
                  const newComps = [...components]
                  newComps.splice(idx, 1)
                  updateComponents(newComps)
                }}
                renderOptionsEditor={renderOptionsEditor}
              />
            ))}
          </SortableContext>
        </DndContext>
        <ComponentAdder onAdd={(c) => updateComponents([...components, c])} existingCount={components.length} />
      </div>

      <LessonFlowPreview components={components} branchLabel={activeBranchLabel} />
    </div>
  )
}
